import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { toast, ToastContainer } from 'react-toastify';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { getAdminMenuItems, getSupervisorMenuItems } from '../../utils/menuItems';

import { ArrowUp, X, Download, Save, Info, User, FileText, CheckCircle, XCircle, Map, Database, GitMerge, FileBadge, Tag, Palette, Users, ShieldCheck, Globe, AlertTriangle, Plus, Trash2, Upload, FileCheck, Send, ClipboardCheck, Edit3 } from "lucide-react";
import config from '../../config/config';
import UnsealProgressModal from '../../components/UnsealProgressModal';
import DataChecklistModal from '../../components/DataChecklistModal';
import MetadataEditor from '../../components/MetadataEditor/MetadataEditor';

import '../../styles/RekapDataDetails.css';
import '../../styles/VerifyDataPage.css';
import 'react-toastify/dist/ReactToastify.css';

// Komponen Modal Konfirmasi
const ConfirmationModal = ({ isOpen, onClose, onConfirm, loading, title, message, confirmText }) => {
  if (!isOpen) return null;

  return (
    <div className="survey-modal-overlay">
      <div className="survey-modal" style={{ maxWidth: '450px' }}>
        <div className="survey-header">
          <div className="survey-header-content">
            <AlertTriangle size={24} className="survey-icon" />
            <div>
              <h3>{title}</h3>
              <p>{message}</p>
            </div>
          </div>
        </div>
        <div className="form-actions" style={{ padding: '20px 30px', borderTop: 'none' }}>
          <button onClick={onClose} className="btn-cancel" disabled={loading}>
            Batal
          </button>
          <button onClick={onConfirm} className="btn-confirm" disabled={loading}>
            {loading ? 'Memproses...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const DataAdminDetailPage = () => {
  const { no_tiket } = useParams();
  const navigate = useNavigate();

  // Detect user role
  const [userRole, setUserRole] = useState(0);
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwtDecode(token);
      setUserRole(decoded.role);
    }
  }, []);

  // States
  const [dataDetail, setDataDetail] = useState(null);
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verificationDocs, setVerificationDocs] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraftAvailable, setIsDraftAvailable] = useState(false);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  // Form states
  const [notes, setNotes] = useState({});
  const [statusChoices, setStatusChoices] = useState({});
  const [verificationNoteFiles, setVerificationNoteFiles] = useState({});
  const [isUnsealModalOpen, setIsUnsealModalOpen] = useState(false);
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [checklistStatuses, setChecklistStatuses] = useState({});
  const [availableChecklists, setAvailableChecklists] = useState({});
  const [fileba, setFileba] = useState('');
  const [filerekom, setFilerekom] = useState('');
  
  // Capaian states
  const [capaianData, setCapaianData] = useState([]);
  const [kepkaInfo, setKepkaInfo] = useState(null);
  
  // Metadata editor states
  const [showMetadataEditor, setShowMetadataEditor] = useState(false);
  const [metadataContent, setMetadataContent] = useState(null);
  const [metadataUrl, setMetadataUrl] = useState('');

  // Array Dokumen
  const documentItems = [
    { label: 'Peta', key: 'peta', icon: <Map className="document-lucide-icon" /> },
    { label: 'Struktur Data', key: 'struktur_data', icon: <Database className="document-lucide-icon" /> },
    { label: 'Mekanisme dan Tata Kerja', key: 'takel', icon: <GitMerge className="document-lucide-icon" /> },
    { label: 'Dokumen Penetapan', key: 'dokumen', icon: <FileBadge className="document-lucide-icon" /> },
    { label: 'Metadata', key: 'metadata', icon: <Tag className="document-lucide-icon" /> },
    { label: 'Simbology', key: 'simbology', icon: <Palette className="document-lucide-icon" /> },
    { label: 'Role Akses', key: 'role', icon: <Users className="document-lucide-icon" /> },
    { label: 'Kontrol Kualitas', key: 'kualitas', icon: <ShieldCheck className="document-lucide-icon" /> },
  ];

  // Fungsi Form Handler
  const handleNoteChange = (e, field) => setNotes({ ...notes, [field]: e.target.value });
  const handleStatusChange = (e, field) => setStatusChoices({ ...statusChoices, [field]: e.target.value });

  const handleVerificationFileChange = (e, docIndex) => {
    const file = e.target.files[0];
    if (file && file.type !== 'application/pdf') {
      toast.error('Hanya file format PDF yang diizinkan.');
      e.target.value = '';
      setVerificationNoteFiles(prev => ({
        ...prev,
        [docIndex]: null
      }));
    } else {
      setVerificationNoteFiles(prev => ({
        ...prev,
        [docIndex]: file
      }));
    }
  };

  //fungsi unsealfile api bssn
  const handleUnsealAndDownload = async () => {
    setIsUnsealModalOpen(true);
  };
  const handleUnsealComplete = () => {
    toast.success('File berhasil diunduh!');
    setIsUnsealModalOpen(false);
  };

  const handleUnsealModalClose = () => {
    setIsUnsealModalOpen(false);
  };

  const handleChecklistOpen = (documentType) => {
    setSelectedDocumentType(documentType);
    setIsChecklistModalOpen(true);
  };

  const handleChecklistClose = async () => {
    setIsChecklistModalOpen(false);
    setSelectedDocumentType('');

    // Refresh checklist status after modal closes
    await refreshChecklistStatus();
  };

  // Handle opening Metadata Editor
  const handleOpenMetadataEditor = async () => {
    if (!dataDetail || !dataDetail.metadata) {
      toast.warning('File metadata tidak tersedia');
      return;
    }
    
    // Check if file is XML
    const metadataUrl = dataDetail.metadata;
    if (!metadataUrl.toLowerCase().endsWith('.xml')) {
      toast.warning('Hanya file XML yang bisa diedit di editor');
      return;
    }
    
    try {
      setMetadataUrl(metadataUrl);
      
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${config.API_URL}/metadata/download`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { noTiket: dataDetail.no_tiket },
          responseType: 'text'
        }
      );
      
      if (!response.data || response.data.trim() === '') {
        toast.error('File metadata kosong atau tidak valid');
        return;
      }
      
      setMetadataContent(response.data);
      setShowMetadataEditor(true);
    } catch (error) {
      console.error('Error loading metadata:', error);
      toast.error('Gagal memuat file metadata: ' + (error.response?.data?.message || error.message));
    }
  };
  
  // Handle closing Metadata Editor
  const handleCloseMetadataEditor = () => {
    setShowMetadataEditor(false);
    setMetadataContent(null);
  };

  // Handle saving metadata from editor
  const handleSaveMetadata = async (xmlContent, values) => {
    try {
      // Convert XML string to Blob
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      
      // Create a File object from the blob
      const file = new File([blob], 'metadata_updated.xml', { type: 'application/xml' });
      
      // Prepare form data
      const formData = new FormData();
      formData.append('metadata', file);
      formData.append('no_tiket', dataDetail.no_tiket);
      
      const token = localStorage.getItem('token');
      
      // Upload updated metadata
      await axios.post(
        `${config.API_URL}/dataAdmin/update-metadata`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Metadata berhasil diperbarui');
      setShowMetadataEditor(false);
      
      // Refresh data detail
      const detailResponse = await axios.get(`${config.API_URL}/dataAdmin/details/${atob(no_tiket)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setDataDetail(detailResponse.data);
    } catch (error) {
      console.error('Error saving metadata:', error);
      toast.error('Gagal menyimpan metadata: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChecklistStatusUpdate = async (documentType, status) => {
    // Only update if this document type has checklist
    if (!availableChecklists[documentType]) {
      console.log(`⚠️ Document ${documentType} does not have checklist, skipping status update`);
      return;
    }

    setChecklistStatuses(prev => ({
      ...prev,
      [documentType]: status
    }));

    // Update status dropdown only for documents with checklist
    setStatusChoices(prev => ({
      ...prev,
      [documentType]: status
    }));

    // Refresh checklist status to ensure sync with DB
    await refreshChecklistStatus();
  };

  const refreshChecklistStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const allDocTypes = [...documentItems.map(i => i.key), 'cakupan'];
      const updatedStatuses = {};

      for (const docType of allDocTypes) {
        // Only check documents that have checklist
        if (!availableChecklists[docType]) {
          continue;
        }

        try {
          const statusResponse = await axios.get(
            `${config.API_URL}/api/checklist/data/${atob(no_tiket)}/${docType}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (statusResponse.data.success && statusResponse.data.checklistData) {
            const status = statusResponse.data.checklistData.status;
            updatedStatuses[docType] = status;
          }
        } catch (error) {
          // No existing checklist data for this document type
        }
      }

      setChecklistStatuses(updatedStatuses);

      // Update status choices ONLY for documents with checklist
      setStatusChoices(prev => {
        const updated = { ...prev };
        Object.keys(updatedStatuses).forEach(docType => {
          // Double check that this document has checklist
          if (availableChecklists[docType] && updatedStatuses[docType]) {
            updated[docType] = updatedStatuses[docType];
          }
        });
        return updated;
      });
    } catch (error) {
      console.error('Error refreshing checklist status:', error);
    }
  };

  // Fungsi untuk mengelola multiple verification documents
  const addVerificationDoc = () => {
    const totalDocs = verificationDocs.length + pendingDocs.length;
    if (totalDocs < 5) {
      const newIndex = Math.max(
        ...verificationDocs.map(doc => doc.index),
        ...pendingDocs.map(doc => doc.index),
        0
      ) + 1;

      setPendingDocs(prev => [...prev, {
        index: newIndex,
        name: `Catatan ${newIndex}`,
        isPending: true
      }]);
    }
  };

  const cancelPendingDoc = (docIndex) => {
    setPendingDocs(prev => prev.filter(doc => doc.index !== docIndex));

    // Also remove file from state if it exists
    setVerificationNoteFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[docIndex];
      return newFiles;
    });
  };

  const confirmPendingDoc = (docIndex) => {
    const pendingDoc = pendingDocs.find(doc => doc.index === docIndex);
    if (pendingDoc && verificationNoteFiles[docIndex]) {
      // Move from pending to verification docs
      setVerificationDocs(prev => [...prev, {
        ...pendingDoc,
        isPending: false
      }]);

      // Remove from pending
      setPendingDocs(prev => prev.filter(doc => doc.index !== docIndex));
    } else {
      toast.error('Silakan pilih file PDF terlebih dahulu.');
    }
  };

  const removeVerificationDoc = async (docIndex) => {
    try {
      const token = localStorage.getItem('token');
      const decodedToken = jwtDecode(token);

      // Call API to delete the document
      await axios.delete(`${config.API_URL}/dataAdmin/deleteVerificationNote/${atob(no_tiket)}/${docIndex}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      // Update local state
      setVerificationDocs(prev => prev.filter(doc => doc.index !== docIndex));

      // Remove file from state if it exists
      setVerificationNoteFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[docIndex];
        return newFiles;
      });

      toast.success(`Catatan ${docIndex} berhasil dihapus!`);
    } catch (err) {
      toast.error('Gagal menghapus dokumen catatan.');
      console.error('Error deleting verification doc:', err);
    }
  };

  // Fungsi Aksi
  // Update capaian function
  const updateCapaian = (index, field, value) => {
    const newCapaian = [...capaianData];
    newCapaian[index][field] = value;
    
    // Recalculate persentase if nilai or target changed
    if (field === 'nilai' || field === 'target') {
      const nilai = parseFloat(newCapaian[index].nilai) || 0;
      const target = parseFloat(newCapaian[index].target) || 0;
      if (target > 0) {
        const calculated = (nilai / target) * 100;
        newCapaian[index].persentase = Math.min(100, calculated).toFixed(2);
      } else {
        newCapaian[index].persentase = 0;
      }
    }
    
    setCapaianData(newCapaian);
  };
  

  const handleSaveConfirmation = () => {
    setModalContent({
      title: 'Konfirmasi Simpan Draf',
      message: 'Apakah Anda yakin ingin menyimpan perubahan ini sebagai draf?',
      confirmText: 'Ya, Simpan',
      action: executeSave,
    });
    setIsConfirmModalOpen(true);
  };

  const handleSubmitConfirmation = () => {
    setModalContent({
      title: 'Konfirmasi Selesai Verifikasi',
      message: 'Tindakan ini akan mengirimkan hasil verifikasi ke supervisor untuk persetujuan. Anda tidak dapat mengubahnya lagi. Lanjutkan?',
      confirmText: 'Ya, Kirim Verifikasi',
      action: executeSubmitVerification,
    });
    setIsConfirmModalOpen(true);
  };

  const executeSave = async (showToast = true) => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      let updatedVerificationDocs = [...verificationDocs];

      // Upload new files
      for (const [docIndex, file] of Object.entries(verificationNoteFiles)) {
        if (file) {
          const fileFormData = new FormData();
          fileFormData.append('verificationNote', file);
          fileFormData.append('username', decodedToken.username);
          fileFormData.append('docIndex', docIndex);

          const uploadResponse = await axios.post(
            `${config.API_URL}/dataAdmin/uploadVerificationNote/${atob(no_tiket)}`,
            fileFormData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              },
            }
          );

          // Update the corresponding document in the array
          const docIndexNum = parseInt(docIndex);
          const existingDocIndex = updatedVerificationDocs.findIndex(doc => doc.index === docIndexNum);

          if (existingDocIndex !== -1) {
            updatedVerificationDocs[existingDocIndex].url = uploadResponse.data.file.url;
          } else {
            updatedVerificationDocs.push({
              index: docIndexNum,
              url: uploadResponse.data.file.url,
              name: `Catatan ${docIndexNum}`
            });
          }
        }
      }

      setVerificationDocs(updatedVerificationDocs);

      // Siapkan data untuk disimpan (include capaian_data)
      const draftData = {
        notes,
        statusChoices,
        username: decodedToken.username,
        no_tiket: atob(no_tiket),
        verificationDocs: updatedVerificationDocs,
        capaian_data: capaianData && capaianData.length > 0 ? capaianData : undefined,
        role: decodedToken.role
      };

      await axios.post(`${config.API_URL}/dataAdmin/saveDraft/${atob(no_tiket)}`, draftData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (showToast) {
        toast.success('Draf berhasil disimpan!');
      }
      setIsDraftAvailable(true);

      // Clear uploaded files from state
      setVerificationNoteFiles({});
      setPendingDocs([]);

      return true; // Return true on success
    } catch (err) {
      toast.error('Gagal menyimpan draf.');
      console.error('Error saving draft:', err);
      return false;
    } finally {
      setIsSaving(false);
      if (showToast) {
        setIsConfirmModalOpen(false);
      }
    }
  };

  const executeSubmitVerification = async () => {
    setIsSubmitting(true);

    // 1. Save draft (verification notes, etc)
    const saveSuccess = await executeSave(false);
    if (!saveSuccess) {
      setIsSubmitting(false);
      toast.error("Gagal menyimpan draf terakhir sebelum mengirim. Proses dibatalkan.");
      setIsConfirmModalOpen(false);
      return;
    }

    // 2. Submit verification (include capaian_data)
    try {
      const token = localStorage.getItem('token');
      const decodedToken = jwtDecode(token);

      const payload = {
        username: decodedToken.username,
        notes,
        statusChoices,
        capaian_data: capaianData && capaianData.length > 0 ? capaianData : undefined
      };

      await axios.post(`${config.API_URL}/dataAdmin/submitVerification/${atob(no_tiket)}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Tiket berhasil diverifikasi dan dikirim ke supervisor!');

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/admin/data');
      }, 2000);

    } catch (err) {
      toast.error('Gagal mengirim hasil verifikasi.');
      console.error('Error submitting verification:', err);
    } finally {
      setIsSubmitting(false);
      setIsConfirmModalOpen(false);
    }
  };


  const downloadVerificationDoc = async (docIndex) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${config.API_URL}/dataAdmin/downloadVerificationDoc/${btoa(dataDetail.no_tiket)}/${docIndex}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Verifikasi_${dataDetail.no_tiket}_Catatan${docIndex}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Gagal mengunduh dokumen verifikasi.');
    }
  };

  // Fungsi Pembantu
  const toggleVisibility = () => window.pageYOffset > 300 ? setIsVisible(true) : setIsVisible(false);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Sesuai':
        return 'status-approved';
      case 'Tidak Sesuai':
        return 'status-rejected';
      case 'Perlu Perbaikan':
        return 'status-needs-improvement';
      default:
        return '';
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) { navigate('/'); return; }
        const decodedToken = jwtDecode(token);
        if (!decodedToken.twoFactorVerified || decodedToken.role !== 0) {
          navigate('/');
          return;
        }

        // Fetch detail data
        const detailResponse = await axios.get(`${config.API_URL}/dataAdmin/details/${atob(no_tiket)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const { user, datacatatan, verification_notes, batte, rekom, capaian_data, kepka_id, is_manual_input } = detailResponse.data;
        setDataDetail(detailResponse.data);
        setProfil(user);
        setFileba(batte || '');
        setFilerekom(rekom || '');
        
        // Load capaian data
        if (capaian_data) {
          try {
            const parsedCapaian = typeof capaian_data === 'string' ? JSON.parse(capaian_data) : capaian_data;
            setCapaianData(parsedCapaian);
          } catch (error) {
            console.error('Error parsing capaian_data:', error);
            setCapaianData([]);
          }
        }
        
        // Load kepka info if available
        if (kepka_id) {
          try {
            const kepkaResponse = await axios.get(`${config.API_URL}/api/management/kepka/${kepka_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            // Backend sends { success: true, data: {...} }
            const kepkaData = kepkaResponse.data.data || kepkaResponse.data;
            setKepkaInfo(kepkaData);
          } catch (error) {
            // Silently handle error - data might not have kepka
            setKepkaInfo(null);
          }
        }

        // Set verification documents from API response
        if (verification_notes && Array.isArray(verification_notes)) {
          setVerificationDocs(verification_notes);
        } else if (detailResponse.data.verification_note) {
          // Backward compatibility for single document
          setVerificationDocs([{
            index: 1,
            url: detailResponse.data.verification_note,
            name: 'Catatan 1'
          }]);
        } else {
          setVerificationDocs([]);
        }

        // Initialize form dengan data kosong terlebih dahulu
        const allKeys = [...documentItems.map(i => i.key), 'cakupan'];
        const initialNotes = {};
        const initialStatusChoices = {};

        allKeys.forEach(key => {
          initialNotes[key] = '';
          initialStatusChoices[key] = 'Tidak Sesuai';
        });

        // Pre-fill form from datacatatan jika ada
        console.log('📋 Datacatatan from API:', datacatatan);
        if (datacatatan && Object.keys(datacatatan).length > 0) {
          console.log('✅ Loading status from datacatatan...');
          allKeys.forEach(key => {
            if (datacatatan[key]) {
              const parts = datacatatan[key].split('|');
              initialNotes[key] = parts[0] || '';
              initialStatusChoices[key] = parts[1] || 'Tidak Sesuai';
              console.log(`  ${key}: note="${parts[0]}", status="${parts[1]}"`);
            }
          });
        } else {
          console.log('⚠️ No datacatatan found, using default "Tidak Sesuai"');
        }

        console.log('📝 Final initialStatusChoices:', initialStatusChoices);
        
        // Set initial values
        setNotes(initialNotes);
        setStatusChoices(initialStatusChoices);

        // Check available checklists for each document type
        const checkAvailableChecklists = async () => {
          const allDocTypes = [...documentItems.map(i => i.key), 'cakupan'];
          const checklistAvailability = {};

          for (const docType of allDocTypes) {
            try {
              const checklistResponse = await axios.get(
                `${config.API_URL}/api/checklist/${docType}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              checklistAvailability[docType] = checklistResponse.data.checklist.length > 0;
            } catch (error) {
              checklistAvailability[docType] = false;
            }
          }

          setAvailableChecklists(checklistAvailability);
          return checklistAvailability;
        };

        // Load existing checklist statuses
        let existingChecklistStatuses = {};
        const hasDatacatatan = datacatatan && Object.keys(datacatatan).length > 0;
        
        const loadChecklistStatuses = async (checklistAvailability) => {
          console.log('🔄 Loading checklist statuses...');
          console.log('Has datacatatan:', hasDatacatatan);
          const allDocTypes = [...documentItems.map(i => i.key), 'cakupan'];
          for (const docType of allDocTypes) {
            // Only load checklist status for documents that have checklist
            if (!checklistAvailability[docType]) {
              console.log(`  ⚠️ ${docType}: No checklist available, skipping`);
              continue;
            }

            try {
              const statusResponse = await axios.get(
                `${config.API_URL}/api/checklist/data/${atob(no_tiket)}/${docType}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );

              if (statusResponse.data.success && statusResponse.data.checklistData) {
                const status = statusResponse.data.checklistData.status;
                existingChecklistStatuses[docType] = status;
                console.log(`  📋 Checklist ${docType}: ${status}`);
              }
            } catch (error) {
              // No existing checklist data for this document type
            }
          }

          setChecklistStatuses(existingChecklistStatuses);

          // Merge checklist status dengan status choices
          // For documents WITH checklist: ALWAYS use checklist status (priority)
          // For documents WITHOUT checklist: use datacatatan or default
          if (Object.keys(existingChecklistStatuses).length > 0) {
            console.log('🔄 Applying checklist statuses...');
            setStatusChoices(prev => {
              const merged = { ...prev }; // prev sudah berisi status dari datacatatan
              
              Object.keys(existingChecklistStatuses).forEach(docType => {
                // Only apply if document has checklist
                if (!checklistAvailability[docType]) {
                  return;
                }

                // For documents with checklist, ALWAYS use checklist status
                merged[docType] = existingChecklistStatuses[docType];
                console.log(`  ✓ ${docType}: Using checklist status = "${existingChecklistStatuses[docType]}"`);
              });
              
              return merged;
            });
          }
        };

        const checklistAvailability = await checkAvailableChecklists();
        await loadChecklistStatuses(checklistAvailability);

        // Check if draft is available
        const currentStatus = detailResponse.data.status;
        console.log('📊 Current data status:', currentStatus);
        
        if (currentStatus === 'Belum Diverifikasi') {
          console.log('🔄 Status "Belum Diverifikasi" - checking for draft...');
          
          // Cek apakah ada datacatatan (dari verifikasi sebelumnya yang mungkin perlu perbaikan)
          const hasDatacatatan = datacatatan && Object.keys(datacatatan).length > 0;
          
          if (hasDatacatatan) {
            console.log('✅ Datacatatan exists (from previous verification) - using datacatatan, not draft');
            // Jika ada datacatatan, berarti ini data yang perlu perbaikan
            // Pakai datacatatan, bukan draft
          } else {
            // Jika tidak ada datacatatan, cek draft
            try {
              const draftResponse = await fetch(`${config.API_URL}/dataAdmin/getDraft/${atob(no_tiket)}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
              });
              if (draftResponse.status === 404) {
                console.log('⚠️ No draft found - using default "Tidak Sesuai"');
                return;
              }

              if (!draftResponse.ok) {
                throw new Error(`HTTP error! status: ${draftResponse.status}`);
              }

              const draftData = await draftResponse.json();

              if (draftData.draft) {
                const draft = draftData.draft;
                console.log('✅ Draft data found:', draft);
                if (draft.notes) {
                  setNotes({ ...initialNotes, ...draft.notes });
                }
                if (draft.statusChoices) {
                  // Merge draft status dengan checklist status, tapi checklist status prioritas
                  console.log('📝 Loading status from draft...');
                  setStatusChoices(prev => {
                    const mergedStatusChoices = { ...draft.statusChoices };
                    // Override dengan checklist status yang sudah ada (checklist prioritas)
                    Object.keys(existingChecklistStatuses).forEach(docType => {
                      if (existingChecklistStatuses[docType]) {
                        mergedStatusChoices[docType] = existingChecklistStatuses[docType];
                        console.log(`  ✓ ${docType}: Overridden by checklist = "${existingChecklistStatuses[docType]}"`);
                      }
                    });

                    return mergedStatusChoices;
                  });
                }
                
                // Handle verification documents from draft
                if (draft.verificationDocs && Array.isArray(draft.verificationDocs)) {
                  setVerificationDocs(draft.verificationDocs);
                } else if (draft.verificationDoc) {
                  // Backward compatibility
                  setVerificationDocs([{
                    index: 1,
                    url: draft.verificationDoc,
                    name: 'Catatan 1'
                  }]);
                }

                setIsDraftAvailable(true);
                console.log('Draft loaded successfully');
              }
            } catch (draftError) {
              if (draftError.response && draftError.response.status === 404) {
                console.log('Tidak ada draft yang tersedia - menggunakan data dari datacatatan atau kosong');
              } else {
                console.error('Error fetching draft:', draftError);
              }
            }
          }
        } else {
          console.log('✅ Status bukan "Belum Diverifikasi" - menggunakan datacatatan, bukan draft');
        }

      } catch (err) {
        console.error('Error fetching data detail:', err);
        if (err.response && err.response.status === 404) {
          setError('Data dengan tiket ini sudah tidak ada atau telah dihapus.');
        } else {
          setError('Gagal memuat detail data. Silakan coba lagi.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, [no_tiket, navigate]);

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div><p>Memuat data...</p></div>;
  if (error) return <div className="error-container"><div className="alert alert-danger">{error}</div></div>;

  const menuItems = userRole === 2 ? getSupervisorMenuItems() : getAdminMenuItems();
  const roleLabel = userRole === 2 ? "Supervisor" : "Admin";

  return (
    <DashboardLayout menuItems={menuItems} userRole={roleLabel}>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/admin">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/admin/data">Daftar Data</a></li>
          <li className="breadcrumb-item active" aria-current="page">Verifikasi Data</li>
        </ol>
      </nav>

      <UnsealProgressModal
        isOpen={isUnsealModalOpen}
        onClose={handleUnsealModalClose}
        onComplete={handleUnsealComplete}
        noTiket={dataDetail?.no_tiket}
        namaIgt={dataDetail?.nama_igt}
      />
      <DataChecklistModal
        isOpen={isChecklistModalOpen}
        onClose={handleChecklistClose}
        documentType={selectedDocumentType}
        noTiket={dataDetail?.no_tiket}
        onStatusUpdate={handleChecklistStatusUpdate}
      />
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={modalContent.action}
        loading={isSaving || isSubmitting}
        title={modalContent.title}
        message={modalContent.message}
        confirmText={modalContent.confirmText}
      />
      <ToastContainer position="top-center" autoClose={3000} theme="colored" />
      <div className="container mt-4">
        {dataDetail && (
          <div className="rekap-detail-wrapper">
            {/* Header Card */}
            <div className="rekap-header-card">
              <button onClick={() => navigate('/admin/data')} className="close-button"><X size={20} /></button>
              <div className="header-content">
                <h2 className="rekap-title">Verifikasi Detail Pengajuan</h2>
                <div className="ticket-info">
                  <span className="ticket-label">No. Tiket:</span>
                  <span className="ticket-number">{dataDetail.no_tiket}</span>
                </div>
              </div>
            </div>

            {/* Info Pengirim */}
            <div className="files-section">
              <h3 className="section-title">
                <User className="me-2" />
                Informasi Pengirim
              </h3>
              <div className="status-summary-card">
                <div className="summary-grid">
                  <div className="summary-item"><label>Nama IGT</label><span>{dataDetail.nama_igt}</span></div>
                  <div className="summary-item"><label>Jenis IGT</label><span>{dataDetail.jenis_igt}</span></div>
                  <div className="summary-item"><label>Kementerian/Lembaga</label><span>{dataDetail.kl}</span></div>
                  <div className="summary-item"><label>Direktorat</label><span>{profil?.direktorat || '-'}</span></div>
                  <div className="summary-item"><label>No. HP</label><span>{profil?.hp || '-'}</span></div>
                  <div className="summary-item"><label>Email</label><span>{profil?.email || '-'}</span></div>
                  <div className="summary-item">
                    <label>Status Verifikasi</label>
                    <span className={`status-badge status-${dataDetail.status?.toLowerCase().replace(' ', '-')}`}>
                      {dataDetail.status}
                    </span>
                  </div>
                  {dataDetail.jenis_igt === 'Renaksi PKSP' && (
                    <div className="summary-item">
                      <label>Status Publikasi</label>
                      {dataDetail.status_publikasi ? (
                        <span className={`status-badge status-${dataDetail.status_publikasi.toLowerCase().replace(/ /g, '-')}`}>
                          {dataDetail.status_publikasi}
                        </span>
                      ) : (
                        <span className="status-badge" style={{ backgroundColor: '#6c757d', color: '#fff' }}>-</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Detail Table Section */}
            <div className="detail-table-section">
              <h3 className="section-title">
                <FileText className="me-2" />
                Formulir Verifikasi Dokumen
              </h3>
                  <div className="table-wrapper">
                    <div className="table-container">
                      <table className="detail-table">
                        <thead>
                          <tr>
                            <th className="col-document">Dokumen</th>
                            <th className="col-file">File Pengajuan</th>
                            <th className="col-notes">Catatan Verifikator</th>
                            <th className="col-status">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documentItems.map((item) => (
                            <tr key={item.key}>
                              <td className="document-cell"><div className="document-info">{item.icon}<span className="dok-name">{item.label}</span></div></td>
                              <td className="file-cell">
                                {dataDetail[item.key] ? (
                                  <div className="file-download-container">
                                    {item.key === 'peta' && dataDetail.status_enkripsi === 'sealed' ? (
                                      <button
                                        onClick={handleUnsealAndDownload}
                                        className="file-download-btn"
                                        disabled={isUnsealModalOpen}
                                      >
                                        <Download size={14} />
                                        <span>{isUnsealModalOpen ? 'Memproses...' : 'Unduh Peta'}</span>
                                      </button>
                                    ) : item.key === 'metadata' ? (
                                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <a
                                          href={`${config.API_URL}/download/${btoa(dataDetail[item.key].split('/').pop())}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="file-download-btn"
                                        >
                                          <FileText size={16} />
                                          <span>Unduh File</span>
                                          <Download size={14} />
                                        </a>
                                        {dataDetail[item.key].toLowerCase().endsWith('.xml') && (
                                          <button
                                            onClick={handleOpenMetadataEditor}
                                            className="file-download-btn"
                                            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
                                          >
                                            <Edit3 size={14} />
                                            <span>Lihat/Edit Metadata</span>
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <a
                                        href={`${config.API_URL}/download/${btoa(dataDetail[item.key].split('/').pop())}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="file-download-btn"
                                      >
                                        <FileText size={16} />
                                        <span>Unduh File {item.label}</span>
                                        <Download size={14} />
                                      </a>
                                    )}
                                  </div>
                                ) : (
                                  <div className="no-file-indicator"><span>File tidak tersedia</span></div>
                                )}
                              </td>
                              <td className="notes-cell">
                                <textarea className="form-control" value={notes[item.key] || ''} onChange={(e) => handleNoteChange(e, item.key)} placeholder="Beri catatan..." rows="2"></textarea>
                              </td>
                              <td className="status-cell">
                                <div className="status-with-checklist">
                                  <select
                                    className={`form-select ${getStatusClass(statusChoices[item.key])} ${checklistStatuses[item.key] ? 'checklist-controlled' : ''}`}
                                    value={statusChoices[item.key] || 'Tidak Sesuai'}
                                    onChange={(e) => handleStatusChange(e, item.key)}
                                    disabled={availableChecklists[item.key] || dataDetail?.status === 'Diverifikasi' || dataDetail?.status === 'Sedang Verifikasi'}
                                    title={availableChecklists[item.key] && checklistStatuses[item.key] ? 'Status dikontrol oleh checklist' : 'Pilih status manual'}
                                    style={{
                                      backgroundColor: statusChoices[item.key] === 'Sesuai' ? '#d1fae5' : statusChoices[item.key] === 'Tidak Sesuai' ? '#fee2e2' : '#f3f4f6',
                                      color: statusChoices[item.key] === 'Sesuai' ? '#059669' : statusChoices[item.key] === 'Tidak Sesuai' ? '#dc2626' : '#6b7280',
                                      fontWeight: '600',
                                      borderColor: statusChoices[item.key] === 'Sesuai' ? '#10b981' : statusChoices[item.key] === 'Tidak Sesuai' ? '#ef4444' : '#d1d5db'
                                    }}
                                  >
                                    <option value="Sesuai">Sesuai</option>
                                    <option value="Tidak Sesuai">Tidak Sesuai</option>
                                  </select>
                                  {availableChecklists[item.key] && (
                                    <button
                                      onClick={() => handleChecklistOpen(item.key)}
                                      className={`checklist-btn-icon ${checklistStatuses[item.key] ? `checklist-${checklistStatuses[item.key].toLowerCase().replace(' ', '-')}` : 'checklist-default'}`}
                                      title={`Checklist: ${checklistStatuses[item.key] || 'Belum dicek'}`}
                                    >
                                      <ClipboardCheck size={14} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {/* Baris Cakupan */}
                          <tr>
                            <td className="document-cell"><div className="document-info"><Globe className="document-lucide-icon" /><span className="dok-name">Cakupan</span></div></td>
                            <td className="file-cell"><div className="cakupan-text-inline">{dataDetail.cakupan || "-"}</div></td>
                            <td className="notes-cell">
                              <textarea className="form-control" value={notes.cakupan || ''} onChange={(e) => handleNoteChange(e, 'cakupan')} placeholder="Beri catatan..." rows="2"></textarea>
                            </td>
                            <td className="status-cell">
                              <div className="status-with-checklist">
                                <select
                                  className={`form-select ${getStatusClass(statusChoices.cakupan)} ${checklistStatuses.cakupan ? 'checklist-controlled' : ''}`}
                                  value={statusChoices.cakupan || 'Tidak Sesuai'}
                                  onChange={(e) => handleStatusChange(e, 'cakupan')}
                                  disabled={availableChecklists.cakupan || dataDetail?.status === 'Diverifikasi' || dataDetail?.status === 'Sedang Verifikasi'}
                                  title={availableChecklists.cakupan && checklistStatuses.cakupan ? 'Status dikontrol oleh checklist' : 'Pilih status manual'}
                                  style={{
                                    backgroundColor: statusChoices.cakupan === 'Sesuai' ? '#d1fae5' : statusChoices.cakupan === 'Tidak Sesuai' ? '#fee2e2' : '#f3f4f6',
                                    color: statusChoices.cakupan === 'Sesuai' ? '#059669' : statusChoices.cakupan === 'Tidak Sesuai' ? '#dc2626' : '#6b7280',
                                    fontWeight: '600',
                                    borderColor: statusChoices.cakupan === 'Sesuai' ? '#10b981' : statusChoices.cakupan === 'Tidak Sesuai' ? '#ef4444' : '#d1d5db'
                                  }}
                                >
                                  <option value="Sesuai">Sesuai</option>
                                  <option value="Tidak Sesuai">Tidak Sesuai</option>
                                </select>
                                {availableChecklists.cakupan && (
                                  <button
                                    onClick={() => handleChecklistOpen('cakupan')}
                                    className={`checklist-btn-icon ${checklistStatuses.cakupan ? `checklist-${checklistStatuses.cakupan.toLowerCase().replace(' ', '-')}` : 'checklist-default'}`}
                                    title={`Checklist: ${checklistStatuses.cakupan || 'Belum dicek'}`}
                                  >
                                    <ClipboardCheck size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
            </div>

            {/* Multiple Catatan Verifikator Section */}
            <div className="files-section">
              <h3 className="section-title"><Download className="me-2" />Dokumen Catatan Verifikasi</h3>
              <div className="verification-docs-container">

                {/* Existing/Confirmed Documents */}
                {verificationDocs.map((doc, i) => (
                  <div key={`existing-${doc.index}`} className="verification-doc-card">
                    <div className="verification-doc-header">
                      <div className="verification-doc-title-section">
                        <FileCheck className="verification-doc-icon" />
                        <span className="verification-doc-title">{`Catatan ${i + 1}`}</span>
                        <span className="verification-doc-status confirmed">Tersimpan</span>
                      </div>
                      <button
                        onClick={() => removeVerificationDoc(doc.index)}
                        className="verification-doc-delete-btn"
                        type="button"
                        title="Hapus dokumen"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Sisa kode di bawah ini tidak perlu diubah */}
                    <div className="verification-doc-content">
                      <div className="verification-doc-upload-section">
                        <label htmlFor={`verificationNote-${doc.index}`} className="verification-upload-label">
                          <Upload className="verification-upload-icon" />
                          Perbarui File (PDF)
                        </label>
                        <input
                          type="file"
                          id={`verificationNote-${doc.index}`}
                          className="verification-file-input"
                          accept=".pdf"
                          onChange={(e) => handleVerificationFileChange(e, doc.index)}
                        />
                      </div>

                      {doc.url && (
                        <div className="verification-doc-download">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              downloadVerificationDoc(doc.index);
                            }}
                            className="verification-download-btn"
                            type="button"
                          >
                            <Download size={14} />
                            <span>Lihat Dokumen</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Pending Documents */}
                {pendingDocs.map((doc) => (
                  <div key={`pending-${doc.index}`} className="verification-doc-card pending">
                    <div className="verification-doc-header">
                      <div className="verification-doc-title-section">
                        <Upload className="verification-doc-icon" />
                        <span className="verification-doc-title">{doc.name}</span>
                        <span className="verification-doc-status pending">Belum Tersimpan</span>
                      </div>
                      <button
                        onClick={() => cancelPendingDoc(doc.index)}
                        className="verification-doc-cancel-btn"
                        type="button"
                        title="Batalkan dokumen"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    <div className="verification-doc-content">
                      <div className="verification-doc-upload-section">
                        <label htmlFor={`verificationNote-${doc.index}`} className="verification-upload-label required">
                          <Upload className="verification-upload-icon" />
                          Pilih File PDF *
                        </label>
                        <input
                          type="file"
                          id={`verificationNote-${doc.index}`}
                          className="verification-file-input"
                          accept=".pdf"
                          onChange={(e) => handleVerificationFileChange(e, doc.index)}
                        />
                      </div>

                      {verificationNoteFiles[doc.index] && (
                        <div className="verification-doc-actions">
                          <button
                            onClick={() => confirmPendingDoc(doc.index)}
                            className="verification-confirm-btn"
                            type="button"
                          >
                            <CheckCircle size={14} />
                            Konfirmasi
                          </button>
                          <div className="verification-file-info">
                            <span>File dipilih: {verificationNoteFiles[doc.index].name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add Document Button */}
                {(verificationDocs.length + pendingDocs.length) < 5 && (
                  <div className="verification-add-section">
                    <button
                      onClick={addVerificationDoc}
                      className="verification-add-btn"
                      type="button"
                    >
                      <Plus size={18} />
                      <span>Tambah Dokumen Catatan</span>
                    </button>
                    <p className="verification-add-help">
                      Anda dapat menambahkan maksimal 5 dokumen catatan verifikasi
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Data Capaian Section */}
            {capaianData && capaianData.length > 0 && (
              <div className="files-section" style={{ marginTop: '30px' }}>
                <h3 className="section-title">
                  <FileCheck className="me-2" size={22} />
                  Data Capaian
                </h3>
                

                
                {/* Capaian Table */}
                <div className="table-wrapper">
                  <div className="table-container">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th style={{ width: '100px', textAlign: 'center' }}>Tahun</th>
                          <th style={{ width: '150px', textAlign: 'center' }}>Capaian</th>
                          <th style={{ width: '150px', textAlign: 'center' }}>Target</th>
                          <th style={{ width: '120px', textAlign: 'center' }}>Satuan</th>
                          <th style={{ width: '120px', textAlign: 'center' }}>Persentase</th>
                        </tr>
                      </thead>
                      <tbody>
                        {capaianData
                          .filter(item => item.nilai && item.nilai !== '-' && item.nilai !== '')
                          .map((item, index) => (
                            <tr key={index}>
                              <td style={{ textAlign: 'center', fontWeight: '600' }}>{item.tahun}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={item.nilai || ''}
                                  onChange={(e) => updateCapaian(index, 'nilai', e.target.value)}
                                  min="0"
                                  step="0.01"
                                  style={{ width: '100%' }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={item.target || ''}
                                  onChange={(e) => updateCapaian(index, 'target', e.target.value)}
                                  min="0"
                                  step="0.01"
                                  style={{ width: '100%' }}
                                  disabled={!!kepkaInfo}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={item.satuan || ''}
                                  onChange={(e) => updateCapaian(index, 'satuan', e.target.value)}
                                  style={{ width: '100%' }}
                                  disabled={!!kepkaInfo}
                                />
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span 
                                  className="badge" 
                                  style={{
                                    backgroundColor: parseFloat(item.persentase) >= 50 ? '#10b981' : '#f59e0b',
                                    color: 'white',
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    fontWeight: '600'
                                  }}
                                >
                                  {item.persentase || 0}%
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Target Info from Kepka - Modern Design */}
                {kepkaInfo && kepkaInfo.target && (
                  <div className="target-capaian-modern">
                    <div className="target-header">
                      <div className="target-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <circle cx="12" cy="12" r="6"/>
                          <circle cx="12" cy="12" r="2"/>
                        </svg>
                      </div>
                      <h4>Target Capaian</h4>
                    </div>
                    <div className="target-grid">
                      {Object.entries(kepkaInfo.target).map(([year, target]) => (
                        <div key={year} className="target-card">
                          <div className="target-year">{year}</div>
                          <div className="target-value">
                            <span className="value-number">{target}</span>
                            <span className="value-unit">{kepkaInfo.satuan}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dokumen Hasil Verifikasi Section */}
            {(filerekom || fileba) && (
              <div className="files-section">
                <h3 className="section-title"><Download className="me-2" />Dokumen Hasil Verifikasi</h3>
                <div className="files-grid">
                  {/* Rekomendasi Hasil Verifikasi */}
                  {filerekom && (
                    <div className="file-card">
                      <div className="file-icon">
                        <Download size={24} />
                      </div>
                      <div className="file-info">
                        <h4>Rekomendasi Hasil Verifikasi</h4>
                        <a
                          href={`${config.API_URL}/downloadsurat/${btoa(filerekom.split('/').pop())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-download-link"
                        >
                          Unduh Dokumen
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Rekomendasi Integrasi dan Berbagi Pakai */}
                  {fileba && (
                    <div className="file-card">
                      <div className="file-icon">
                        <Download size={24} />
                      </div>
                      <div className="file-info">
                        <h4>Rekomendasi Integrasi dan Berbagi Pakai</h4>
                        <a
                          href={`${config.API_URL}/downloadsurat/${btoa(fileba.split('/').pop())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-download-link"
                        >
                          Unduh Dokumen
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tombol Aksi */}
            <div className="action-wrapper" style={{ marginTop: '30px', marginBottom: '30px' }}>
              <div className="action-buttons-container" style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={handleSaveConfirmation}
                  className="btn-submit"
                  disabled={
                    isSaving || 
                    isSubmitting || 
                    dataDetail?.status === 'Sedang Verifikasi' || 
                    dataDetail?.status === 'Perlu Perbaikan'
                  }
                  style={{ minWidth: '180px' }}
                >
                  <Save size={16} />
                  {isSaving ? 'Menyimpan...' : 'Simpan Draf'}
                </button>
                <button
                  onClick={handleSubmitConfirmation}
                  className="btn-submit btn-submit-verification"
                  disabled={
                    isSaving || 
                    isSubmitting || 
                    dataDetail?.status === 'Sedang Verifikasi' || 
                    dataDetail?.status === 'Perlu Perbaikan'
                  }
                  style={{ minWidth: '180px' }}
                >
                  <Send size={16} />
                  {isSubmitting ? 'Mengirim...' : 'Selesai Verifikasi'}
                </button>
              </div>
              {(dataDetail?.status === 'Sedang Verifikasi' || dataDetail?.status === 'Perlu Perbaikan') && (
                <div className="approval-status-label" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '16px',
                  padding: '12px 20px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '8px',
                  color: '#92400e',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <span className="status-icon-pulse" style={{
                    display: 'inline-flex',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }}>
                    <Info size={14} />
                  </span>
                  <span>
                    {dataDetail?.status === 'Sedang Verifikasi' 
                      ? 'Menunggu persetujuan supervisor.' 
                      : 'Menunggu perbaikan dari K/L.'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {isVisible && <button onClick={scrollToTop} className="scroll-to-top"><ArrowUp size={24} /></button>}
      
      {showMetadataEditor && (
        <MetadataEditor
          isOpen={showMetadataEditor}
          onClose={handleCloseMetadataEditor}
          metadataFile={metadataContent}
          onSave={handleSaveMetadata}
          readOnly={dataDetail?.status === 'Sedang Verifikasi' || dataDetail?.status === 'Perlu Perbaikan'}
        />
      )}
    </DashboardLayout>
  );
};

export default DataAdminDetailPage;